import * as React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import { interval } from 'rxjs'
import Icon from 'components/Icon'
import { IconButton } from 'components/IconButton'
import { expr } from 'mobx-utils'
import {
  SplitViewHeader,
  SplitViewHeaderTitle,
  SplitViewHeaderSubtitle,
} from 'components/SplitView'
import { Dropdown, DropdownItem } from 'components/Dropdown'
import Tooltip from 'components/Tooltip'
import Spinner from 'components/Spinner'
import { timeago } from 'utils/date-util'
import { useObservableStream } from 'hooks/rx-hooks'
import JobCreationViewModel from '../../../JobCreationViewModel'
import Job from '../../../../Job'
import { PublishedLegend } from 'legends/PublishedLegend'
import styles from './Header.sass'

interface HeaderProps {
  vm: JobCreationViewModel
}

/**
 * The Header
 */
export default observer(function Header({ vm }: HeaderProps) {
  const backClick = React.useCallback(() => {
    vm.outline.goToPrevious()
  }, [vm])

  // Show progress only when we have the Legend and after the Draft
  // has been created.
  const showingProgress = expr(
    () =>
      vm.legendSelector.selectedLegendVersion !== PublishedLegend.none &&
      !vm.intakeQuestions.questionList.isBroken &&
      !vm.intakeQuestions.isRunningFirstQuery &&
      vm.job !== Job.none,
  )

  return (
    <SplitViewHeader
      progress={showingProgress ? vm.outline.progress.percent : undefined}
    >
      <div className={styles.root}>
        <div className={styles.extra}>
          <Tooltip
            placement="bottom"
            content={
              vm.outline.previousStep?.step.outlineDisplay.title ??
              'Unable to go back, this is the first step.'
            }
          >
            <IconButton onClick={backClick} disabled={!vm.outline.previousStep}>
              <Icon name="back" />
            </IconButton>
          </Tooltip>
        </div>
        <div className={styles.main}>
          <SplitViewHeaderTitle>
            {vm.outline.activeStep?.outlineDisplay.title || 'Welcome'}
          </SplitViewHeaderTitle>
          {showingProgress && (
            <SplitViewHeaderSubtitle>
              {vm.outline.progress.percent}% completed
            </SplitViewHeaderSubtitle>
          )}
        </div>
        <div className={styles.extra}>
          <PersistingStatusContainer vm={vm} />
          {vm.job !== Job.none && (
            <Dropdown
              position="bottom-end"
              autoDismiss
              disabled={vm.persister.persisting}
              button={
                <IconButton data-test="draft-header-actions-dropdown-toggle">
                  <Icon name="more" rotate={90} />
                </IconButton>
              }
            >
              <DropdownItem
                data-test="draft-header-rename-job-button"
                icon={<Icon name="rename-job" />}
                onClick={vm.renameModal.show}
                shortcut="F2"
              >
                Rename
              </DropdownItem>
              <DropdownItem
                data-test="draft-header-duplicate-button"
                icon={<Icon name="copy" />}
                onClick={vm.copyModal.show}
              >
                Clone
              </DropdownItem>
              <DropdownItem
                icon={<Icon name="delete" />}
                onClick={vm.deleteModal.show}
              >
                Delete
              </DropdownItem>
            </Dropdown>
          )}
        </div>
      </div>
    </SplitViewHeader>
  )
})

const formatter = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
})

const PersistingStatusContainer = observer(function PersistingStatusContainer({
  vm,
}: HeaderProps) {
  useObservableStream(
    0,
    // Refreshes every minute + 1 second
    interval(61000),
  )

  if (vm.persister.persisting) {
    return <PersistingStatus persisting />
  }

  if (vm.persister.lastPersistResult.errorMessage) {
    return (
      <PersistingStatus
        error
        title={vm.persister.lastPersistResult.errorMessage}
      >
        Saving failed
      </PersistingStatus>
    )
  }

  if (!vm.persister.jobWasCreated) {
    return null
  }

  return (
    <PersistingStatus
      title={`Last saved ${formatter.format(vm.persister.lastSavedAt)}`}
    >
      Saved {formatDateModified(vm.persister.lastSavedAt)}
    </PersistingStatus>
  )
})

interface PersistingStatusProps {
  error?: boolean
  persisting?: boolean
  title?: string
  children?: React.ReactNode
}

function PersistingStatus({
  error,
  persisting,
  title,
  children,
}: PersistingStatusProps) {
  return (
    <div className={cx(styles.status, error && styles.error)}>
      {error && (
        <div className={styles.statusIcon}>
          <Icon name="info" />
        </div>
      )}
      {persisting && (
        <div className={styles.statusIcon}>
          <Spinner size={16} />
        </div>
      )}
      <div className={styles.message} title={title}>
        {children}
      </div>
    </div>
  )
}

function formatDateModified(date: Date) {
  const formatted = timeago(date)
  if (formatted === 'now') return 'just now'
  return `${formatted} ago`
}
